// Grid inline de productos del usuario — reemplaza al sheet separado
// que había antes. Vive dentro del mismo panel lateral / bottom sheet del
// preset, así no hay un modal encima de otro modal.
//
// Tiene dos variantes:
//   • mobile  → iOS-style bottom sheet: drag handle pill + header con
//               "Cancelar" a la izquierda y título "Mis productos" al
//               centro (mismo patrón que los menús de PruebaNuevaInterfaz
//               y EditarImagen). Toma el panel completo sin padding
//               externo.
//   • desktop → versión simple inline: "Volver" link arriba + título +
//               grid. Vive dentro de la options card del split horizontal.
//
// Si el preset trae `tiposProductoSugeridos` y el rubro es de moda,
// separamos en sección "Sugeridos" (matchean model_view) y "Otros".

import React, { useMemo, useState } from 'react';
import { Check, ChevronLeft, Sparkles } from 'lucide-react';
import { useUserProducts } from '../productsContext';
import { getLastUsedProductIds, sortLastUsedFirst } from '../lastUsedProducts';
import type { MockProductV2 } from '../mockData';

export interface PickProductPanelProps {
  variant: 'mobile' | 'desktop';
  /** El user confirma su selección (uno o varios productos). */
  onConfirm: (products: MockProductV2[]) => void;
  onBack: () => void;
  /** Si el template tiene tipos sugeridos y el rubro califica, separamos
   *  los productos del user en "Sugeridos" (matchean model_view) y
   *  "Otros productos". */
  tiposProductoSugeridos?: string[];
  rubro?: string | null;
  /** Selección inicial (ids, en orden — el primero es el "principal").
   *  Permite reabrir el picker con lo que ya estaba elegido. */
  initialSelectedIds?: string[];
  /** Si true, el primer seleccionado se marca como "Principal" en su tile
   *  (usado al combinar productos en catálogo). */
  showPrimary?: boolean;
  /** Oculta el link "Volver" del header desktop (cuando el sheet ya tiene
   *  su propio cierre, p.ej. una X). */
  hideBack?: boolean;
  /** Título del header desktop. Default "Elegí los productos". */
  title?: string;
  /** Subtítulo / explicación de qué se está seleccionando. */
  description?: string;
}

export const PickProductPanel: React.FC<PickProductPanelProps> = ({
  variant,
  onConfirm,
  onBack,
  tiposProductoSugeridos,
  rubro,
  initialSelectedIds,
  showPrimary = false,
  hideBack = false,
  title = 'Elegí los productos',
  description = 'Podés elegir uno o varios.',
}) => {
  // Products reales del user — vienen del ProductsContext (poblado por
  // InterfazV2 via fetchUserProducts). Si no hay productos cargados, la
  // grilla queda vacía.
  const rawProducts = useUserProducts();
  // Productos usados en la última generación — flotan arriba y llevan un
  // badge "Último utilizado". Leemos los ids una sola vez al montar.
  const lastUsedIds = useMemo(() => getLastUsedProductIds(), []);
  const lastUsedSet = useMemo(() => new Set(lastUsedIds), [lastUsedIds]);
  const products = useMemo(
    () => sortLastUsedFirst(rawProducts, lastUsedIds),
    [rawProducts, lastUsedIds],
  );
  // Multi-selección por toggle. Mantenemos el ORDEN: el primer elemento es
  // el "principal". Al quitar el principal, el siguiente pasa a serlo; al
  // vaciar todo, el próximo en seleccionarse será el nuevo principal.
  const [selectedIds, setSelectedIds] = useState<string[]>(
    () => initialSelectedIds ?? [],
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const handleConfirm = () => {
    if (selectedIds.length === 0) return;
    // Devolvemos en el ORDEN de selección (no el orden del catálogo) para
    // preservar cuál es el principal (primer elemento).
    const byId = new Map(products.map((p) => [p.id, p]));
    onConfirm(
      selectedIds.map((id) => byId.get(id)).filter(Boolean) as MockProductV2[],
    );
  };

  // ── Sugerencias por tipo de producto ────────────────────────────────
  // Solo para rubros de moda — sería ruidoso aplicarlo a otros rubros
  // donde model_view casi no se completa. Si el template no tiene tipos
  // sugeridos o no hay matches, mostramos un único grid sin sección.
  const SUGGEST_RUBROS = new Set(['Moda e Indumentaria']);
  const shouldSuggest =
    !!rubro &&
    SUGGEST_RUBROS.has(rubro) &&
    Array.isArray(tiposProductoSugeridos) &&
    tiposProductoSugeridos.length > 0;
  const suggestedSet = shouldSuggest
    ? new Set(
        (tiposProductoSugeridos as string[]).map((s) =>
          s.trim().toLowerCase(),
        ),
      )
    : null;
  const { suggested, others } = (() => {
    if (!suggestedSet) {
      return { suggested: [] as MockProductV2[], others: products };
    }
    const sug: MockProductV2[] = [];
    const oth: MockProductV2[] = [];
    for (const p of products) {
      const mv = (p.modelView || '').trim().toLowerCase();
      if (mv && suggestedSet.has(mv)) {
        sug.push(p);
      } else {
        oth.push(p);
      }
    }
    return { suggested: sug, others: oth };
  })();
  const hasSuggestions = suggested.length > 0;

  // Tile reutilizado en ambos viewports. Rounded varia (xl mobile · 2xl
  // desktop) para mantener el look existente.
  const renderTile = (p: MockProductV2, radius: 'xl' | '2xl') => {
    const isSel = selectedIds.includes(p.id);
    const isPrimary = showPrimary && isSel && selectedIds[0] === p.id;
    const isLastUsed = lastUsedSet.has(p.id);
    const rounded = radius === 'xl' ? 'rounded-xl' : 'rounded-2xl';
    return (
      <button
        key={p.id}
        type="button"
        onClick={() => toggle(p.id)}
        className={`text-left ${rounded} overflow-hidden bg-white border-2 ${
          isSel
            ? 'border-brand-primary'
            : radius === '2xl'
              ? 'border-neutral-200 hover:border-brand-primary/40'
              : 'border-neutral-200'
        } active:scale-[0.98] transition-all relative`}
      >
        <div className="relative aspect-square bg-neutral-100">
          <img
            src={p.imageUrl}
            alt={p.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {isLastUsed && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-brand-black text-white text-[10px] font-semibold shadow">
              Último utilizado
            </span>
          )}
          {isSel && (
            <div className="absolute top-2 right-2 flex items-center gap-1.5">
              {isPrimary && (
                <span className="px-2 py-0.5 rounded-full bg-brand-primary text-white text-[10.5px] font-semibold shadow">
                  Principal
                </span>
              )}
              <div className="w-6 h-6 rounded-full bg-brand-primary flex items-center justify-center shadow">
                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              </div>
            </div>
          )}
        </div>
        <div className="px-3 py-2.5">
          <p className="text-[13.5px] font-medium text-brand-black/90 truncate">
            {p.name}
          </p>
        </div>
      </button>
    );
  };

  if (variant === 'mobile') {
    return (
      <>
        {/* Drag handle — pill gris centrada arriba. */}
        <div className="py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* iOS header: Cancelar (azul, izq) · título centrado · Confirmar (der). */}
        <div className="flex items-center justify-between px-4 pb-3 shrink-0">
          <button
            type="button"
            onClick={onBack}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            Mis productos
          </h3>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedIds.length === 0}
            className="text-[15px] text-brand-primary font-semibold disabled:opacity-40 active:opacity-50 transition-opacity w-20 text-right"
          >
            {selectedIds.length > 0
              ? `Listo (${selectedIds.length})`
              : 'Listo'}
          </button>
        </div>
        {/* Subtítulo con hint multi-select. */}
        <div className="px-4 pb-5 shrink-0">
          <p className="text-[12.5px] text-[#3C3C43]/55">{description}</p>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-6">
          {hasSuggestions ? (
            <>
              <div className="flex items-center gap-1.5 mb-2 mt-1">
                <Sparkles className="w-3.5 h-3.5 text-[#3C3C43]/55" />
                <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55">
                  Sugeridos para esta plantilla
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {suggested.map((p) => renderTile(p, 'xl'))}
              </div>
              {others.length > 0 && (
                <>
                  <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55 mt-5 mb-2">
                    Otros productos
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {others.map((p) => renderTile(p, 'xl'))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {products.map((p) => renderTile(p, 'xl'))}
            </div>
          )}
        </div>
      </>
    );
  }

  // Desktop variant — layout self-contained:
  //   • Header (Volver + título + subtítulo) — shrink-0
  //   • Grilla scrolleable — flex-1 overflow-y-auto
  //   • Footer con Confirmar — shrink-0, border-top
  // Importante: el aside padre NO debe envolver esto en otro overflow-y
  // (ver la rama `step === 'pick-product'` del PresetDetailSheet desktop).
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 px-6 pt-6 pb-5">
        {!hideBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-brand-primary text-[13px] mb-3 hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Volver
          </button>
        )}
        <p className="text-[16px] font-semibold text-brand-black/90">
          {title}
        </p>
        <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">
          {description}
        </p>
      </div>

      {/* Scrollable grid */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6">
        {hasSuggestions ? (
          <div className="pb-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-[#3C3C43]/55" />
              <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55">
                Sugeridos para esta plantilla
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {suggested.map((p) => renderTile(p, '2xl'))}
            </div>
            {others.length > 0 && (
              <>
                <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55 mt-5 mb-2">
                  Otros productos
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {others.map((p) => renderTile(p, '2xl'))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 pb-3">
            {products.map((p) => renderTile(p, '2xl'))}
          </div>
        )}
      </div>

      {/* Footer fijo — siempre visible, ancla el botón al bottom del aside */}
      <div className="shrink-0 px-6 pt-3 pb-4 border-t border-black/[0.05] bg-white">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={selectedIds.length === 0}
          className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
        >
          <span>Confirmar</span>
          {selectedIds.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-white/25 text-[12px] font-bold leading-none">
              {selectedIds.length}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};
